import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ConfirmDeleteModal from "../detail/ConfirmDeleteModal";

export function EditPageLayout({
  breadcrumbs = [],
  title,
  subtitle,
  detailPath,
  listPath,
  backLabel,
  onRemove,
  loading = false,
  submitLabel = "Update",
  onSubmit,
  children,
  removeMessage,
}) {
  const navigate = useNavigate();
  const [openRemove, setOpenRemove] = useState(false);
  const backPath = detailPath || listPath;
  const resolvedBackLabel =
    backLabel || (detailPath ? "Back to Details" : "Back to List");

  const handleConfirmRemove = async () => {
    setOpenRemove(false);
    if (onRemove) {
      await onRemove();
    }
  };

  return (
    <form className="edit-page" onSubmit={onSubmit}>
      <header className="edit-header">
        <div className="edit-header-left">
          {breadcrumbs.length > 0 && (
            <nav className="edit-breadcrumb" aria-label="breadcrumb">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={`${crumb.label}-${index}`}>
                  {index > 0 && (
                    <span className="edit-breadcrumb-separator">&gt;</span>
                  )}
                  {crumb.path ? (
                    <Link to={crumb.path}>{crumb.label}</Link>
                  ) : (
                    <span className="edit-breadcrumb-current">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          )}
          <h1 className="edit-header-title">{title}</h1>
          {subtitle && <p className="edit-header-subtitle">{subtitle}</p>}
        </div>

        {backPath && (
          <div className="edit-header-actions">
            <button
              type="button"
              className="edit-btn edit-btn-back"
              onClick={() => navigate(backPath)}
            >
              <ArrowBackIcon fontSize="small" />
              {resolvedBackLabel}
            </button>
          </div>
        )}
      </header>

      <div className="edit-page-main">
        <div className="edit-page-card">{children}</div>
      </div>

      {(onRemove || submitLabel) && (
        <footer className="edit-footer-actions">
          {onRemove && (
            <button
              type="button"
              className="edit-btn edit-btn-remove-outline"
              onClick={() => setOpenRemove(true)}
            >
              <DeleteOutlineIcon fontSize="small" />
              Remove
            </button>
          )}
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            className="edit-btn-submit"
            startIcon={<CheckIcon fontSize="small" />}
          >
            {submitLabel}
          </LoadingButton>
        </footer>
      )}

      {onRemove && (
        <ConfirmDeleteModal
          open={openRemove}
          onClose={() => setOpenRemove(false)}
          onConfirm={handleConfirmRemove}
          message={removeMessage}
        />
      )}
    </form>
  );
}

export function EditFormTopSection({ image, children }) {
  return (
    <div className="edit-form-top">
      {image}
      <div className="edit-form-top-fields">{children}</div>
    </div>
  );
}

export function EditFormParagraphsSection({ children }) {
  return <div className="edit-form-paragraphs">{children}</div>;
}

export function EditImageSection({
  uploadLabel = "Image",
  previewLabel = "Image Preview",
  imageUrl,
  imagePreview,
  onFileChange,
  error,
  recommendedSize,
  accept = "image/png, image/jpeg, image/jpg, image/webp, image/gif",
  inputId = "edit-image-input",
}) {
  const displayImage = imagePreview || imageUrl;

  return (
    <div className="edit-image-column">
      <span className="edit-field-label">{previewLabel}</span>
      <div className="edit-image-preview-wrap">
        {displayImage ? (
          <img className="edit-image-preview" src={displayImage} alt="" />
        ) : (
          <div className="edit-image-preview-empty">No image selected</div>
        )}
      </div>

      <label className="edit-field-label" htmlFor={inputId}>
        {uploadLabel}
      </label>
      <label className="edit-image-dropzone" htmlFor={inputId}>
        <CloudUploadOutlinedIcon className="edit-image-dropzone-icon" />
        <span className="edit-image-dropzone-title">
          Click to upload or drag and drop
        </span>
        <span className="edit-image-dropzone-hint">
          PNG, JPG or WEBP (Max 5MB)
        </span>
      </label>
      <input
        id={inputId}
        type="file"
        className="edit-image-input"
        accept={accept}
        onChange={onFileChange}
      />
      {recommendedSize && (
        <p className="edit-image-recommended">
          Recommended size: {recommendedSize}
        </p>
      )}
      {error && <p className="edit-field-error">{error}</p>}
    </div>
  );
}

export function EditFieldLabel({ children, required }) {
  return (
    <label className="edit-field-label">
      {children}
      {required && <span className="edit-field-required">*</span>}
    </label>
  );
}
